<script setup>
import {
  ref,
  computed,
  reactive,
  nextTick,
  onMounted,
  onUnmounted,
  onActivated,
  onDeactivated,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import powerDataAPI from 'dashboard/api/powerData';
import { fraseDaRecusa } from 'dashboard/helper/recusaDoAcesso';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FilterPill from 'dashboard/components-next/filter-bar/FilterPill.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import SourceEditDialog from './SourceEditDialog.vue';
import { useRelativeTime } from '../composables/useRelativeTime';
import { montarCsvQa } from '../helpers/csvQa';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const props = defineProps({
  storeId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const { relativeTime } = useRelativeTime();

const accountId = store.getters.getCurrentAccountId;

// Um quadro REALMENTE pintado antes do trabalho pesado.
//
// Sem isto o indicador nunca aparece, e o motivo e sutil: ligar o flag e
// expandir o site acontecem na mesma tarefa, o Vue junta as duas coisas num
// unico flush e o navegador so pinta quando tudo terminou — ou seja, o
// "carregando" apareceria e sumiria depois da espera que ele existe para
// cobrir. Dois requestAnimationFrame porque um so agenda ANTES do proximo
// paint; o segundo so roda DEPOIS dele, que e a garantia que queremos.
const proximoQuadroPintado = () =>
  new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

const sources = ref([]);
const isFetching = ref(true);
// Separa "a base esta vazia" de "nao consegui ler a base". Sem isso as duas
// situacoes mostravam a MESMA frase, e uma base com 1.186 fontes aparecia como
// "nenhuma source ainda" quando o backend estava fora.
const fetchFalhou = ref(false);
const search = ref('');
const filterType = ref('');
const filterStatus = ref('');
const expandedSites = reactive(new Set());
const loading = ref({});

const editDialogRef = ref(null);
const editingSource = ref(null);
const deleteDialogRef = ref(null);
const sourceToDelete = ref(null);
// Apagar um por um numa base de 50 arquivos é castigo. A seleção vive num Set
// de ids, e apagar em lote reaproveita o mesmo diálogo de confirmação.
const selecionadas = ref(new Set());
const exportandoQa = ref(false);
const bulkDialogRef = ref(null);
const isBulkDeleting = ref(false);

// `transcribing` (PD-02) conta como processando: mantém o poll vivo até a mídia terminar.
const PROCESSING_STATES = ['pending', 'processing', 'scraping', 'transcribing'];
const FONTES_DE_MIDIA = ['audio', 'video'];
const DONE_STATES = ['ready', 'extracted'];

const typeOptions = computed(() => [
  { id: 'file', name: t('POWER_DATA.SOURCE_TYPE.FILE') },
  { id: 'text', name: t('POWER_DATA.SOURCE_TYPE.TEXT') },
  { id: 'qa', name: t('POWER_DATA.SOURCE_TYPE.QA') },
  { id: 'site', name: t('POWER_DATA.SOURCE_TYPE.SITE') },
  { id: 'scrape', name: t('POWER_DATA.SOURCE_TYPE.PAGE') },
  { id: 'audio', name: t('POWER_DATA.SOURCE_TYPE.AUDIO') },
  { id: 'video', name: t('POWER_DATA.SOURCE_TYPE.VIDEO') },
]);

const statusOptions = computed(() => [
  { id: 'ready', name: t('POWER_DATA.STATUS.READY') },
  { id: 'processing', name: t('POWER_DATA.STATUS.PROCESSING') },
  { id: 'pending', name: t('POWER_DATA.STATUS.PENDING') },
  { id: 'transcribing', name: t('POWER_DATA.STATUS.TRANSCRIBING') },
  { id: 'error', name: t('POWER_DATA.STATUS.ERROR') },
]);

// A pílula trabalha com lista; aqui o filtro é um valor só, e '' quer dizer
// "todos". O resto da tela segue lendo filterType e filterStatus como texto.
const comoLista = valor =>
  computed({
    get: () => (valor.value ? [valor.value] : []),
    set: lista => {
      valor.value = lista[0] ?? '';
    },
  });
const filterTypeLista = comoLista(filterType);
const filterStatusLista = comoLista(filterStatus);

const tableHeaders = computed(() => [
  '',
  t('POWER_DATA.SOURCES.NAME'),
  t('POWER_DATA.SOURCES.TYPE'),
  t('POWER_DATA.SOURCES.CHUNKS'),
  t('POWER_DATA.SOURCES.UPDATED'),
  t('POWER_DATA.SOURCES.STATUS'),
  '',
]);

// ── Apresentação ────────────────────────────────────────────────
// Os passos -3/-4 de fundo com -11 de texto são os mesmos que o resto do OS usa
// para selo de estado. Nada de verde/vermelho puro do Tailwind.
function statusClass(status) {
  if (DONE_STATES.includes(status)) return 'bg-n-teal-4 text-n-teal-11';
  if (PROCESSING_STATES.includes(status)) return 'bg-n-amber-4 text-n-amber-11';
  if (status === 'error') return 'bg-n-ruby-4 text-n-ruby-11';
  return 'bg-n-slate-4 text-n-slate-11';
}

function statusLabel(status) {
  const key = `POWER_DATA.STATUS.${String(status || '').toUpperCase()}`;
  const label = t(key);
  return label === key ? status : label;
}

function isProcessing(row) {
  return PROCESSING_STATES.includes(row.status);
}

// O status virou simbolo: uma linha de 1.186 nao precisa repetir a palavra
// "Pronta" 1.185 vezes para dizer que esta tudo bem — o que interessa de
// relance e a excecao. A palavra continua existindo, no tooltip e no
// aria-label, entao nada de informacao se perdeu, so mudou de camada.
function statusIcon(status) {
  if (DONE_STATES.includes(status)) return 'i-lucide-check';
  if (status === 'error') return 'i-lucide-triangle-alert';
  return 'i-lucide-circle-help';
}

// Circunferencia do anel: r=8 no viewBox de 20. Constante porque o raio e
// fixo no SVG abaixo — se um mudar, o outro tem que mudar junto.
const RAIO_ANEL = 8;
const VOLTA_ANEL = 2 * Math.PI * RAIO_ANEL;

function progressoDe(row) {
  const pct = Number(row.progress);
  if (!Number.isFinite(pct)) return 0;
  return Math.min(100, Math.max(0, pct));
}

function anelOffset(row) {
  return VOLTA_ANEL * (1 - progressoDe(row) / 100);
}

// A mídia (PD-02) guarda o erro como código (`sem_fala`, `sem_chave`...), e a tela diz a frase.
// Código sem tradução, ou fonte que não é mídia, mostra a mensagem como veio.
function motivoLegivel(row) {
  if (!FONTES_DE_MIDIA.includes(row.source_type)) return row.error_message;
  const key = `POWER_DATA.MIDIA.MOTIVO.${String(row.error_message).toUpperCase()}`;
  const frase = t(key);
  return frase === key ? row.error_message : frase;
}

// O texto que o simbolo deixou de mostrar. Processando leva a porcentagem
// REAL (o backend grava por lote de embedding em _set_progress, e o site pai
// tem escala propria: 0-50% baixando paginas, 50-100% indexando), e erro leva
// a mensagem, que era o unico tooltip que existia antes.
function statusTooltip(row) {
  const label = statusLabel(row.status);
  if (isProcessing(row)) return `${label} — ${progressoDe(row)}%`;
  if (row.status === 'error' && row.error_message) {
    return `${label} — ${motivoLegivel(row)}`;
  }
  return label;
}

// "Tentar de novo": a mídia em erro é transcrita outra vez a partir do original guardado.
function podeTranscrever(row) {
  return FONTES_DE_MIDIA.includes(row.source_type) && row.status === 'error';
}

// A ORIGEM da source, não o formato do arquivo. O fallback antigo para
// `file_type` era o que fazia toda linha dizer "Documento": texto colado e Q&A
// viajam como .txt, então pelo formato os três são indistinguíveis.
// `scrape` é a página adicionada sozinha (o caminho do cliente). Não tinha tradução, e a linha
// mostrava a palavra crua.
function typeLabel(row) {
  const kind =
    row.source_type === 'scrape' ? 'page' : row.source_type || 'file';
  const key = `POWER_DATA.SOURCE_TYPE.${kind.toUpperCase()}`;
  const label = t(key);
  return label === key ? kind : label;
}

// Reprocessar só faz sentido quando há de onde rebuscar: fonte web muda na
// origem. Um arquivo enviado não muda sozinho — reprocessá-lo só gasta API.
function podeReprocessar(row) {
  return row.source_type === 'site' || row.source_type === 'page';
}

function iconForType(row) {
  if (row.source_type === 'site' || row.file_type === 'folder')
    return 'i-lucide-folder';
  if (row.source_type === 'page' || row.source_type === 'scrape')
    return 'i-lucide-globe';
  if (row.source_type === 'audio') return 'i-lucide-file-audio';
  if (row.source_type === 'video') return 'i-lucide-file-video';
  return 'i-lucide-file-text';
}

function displayName(row) {
  if (row.file_type === 'folder' || row.source_type === 'site')
    return row.filename || row.scrape_url || '—';
  const url = row.scrape_url;
  if (url && !row.filename) {
    try {
      return new URL(url).pathname.replace(/\/$/, '') || '/';
    } catch {
      return url;
    }
  }
  return row.filename || '—';
}

// ── Dados ───────────────────────────────────────────────────────
async function fetchSources() {
  try {
    const data = await powerDataAPI.sourcesIndex(accountId, props.storeId);
    const lista = Array.isArray(data) ? data : data?.sources;
    // Array.isArray e nao `|| data`: o que vier fora do formato NAO substitui a
    // lista. A versao anterior aceitava qualquer coisa truthy, entao um corpo de
    // erro virava `sources`, e o `.some`/`.filter` seguinte estourava.
    if (!Array.isArray(lista)) throw new Error('resposta fora do formato');
    sources.value = lista;
    fetchFalhou.value = false;
  } catch (e) {
    // A lista anterior fica na tela: um poll que falha nao pode apagar o que ja
    // estava certo. Quem distingue "vazia" de "nao consegui ler" e fetchFalhou.
    fetchFalhou.value = true;
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.SOURCES.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
}

const treeRows = computed(() => {
  const all = sources.value;
  let filtered = all;

  if (search.value) {
    const q = search.value.toLowerCase();
    filtered = filtered.filter(
      s =>
        s.filename?.toLowerCase().includes(q) ||
        s.scrape_url?.toLowerCase().includes(q) ||
        (s.tags || []).some(tag => tag.toLowerCase().includes(q))
    );
  }
  if (filterType.value)
    filtered = filtered.filter(s => s.source_type === filterType.value);
  if (filterStatus.value)
    filtered = filtered.filter(s => s.status === filterStatus.value);

  const roots = [];
  const children = {};
  filtered.forEach(item => {
    if (item.parent_source_id) {
      (children[item.parent_source_id] ||= []).push(item);
    } else {
      roots.push(item);
    }
  });

  const rows = [];
  roots.forEach(root => {
    const isFolder = root.source_type === 'site' || root.file_type === 'folder';
    const allKids = all.filter(item => item.parent_source_id === root.id);
    rows.push({
      ...root,
      _depth: 0,
      _isFolder: isFolder,
      _childCount: allKids.length,
      _childDone: allKids.filter(k => DONE_STATES.includes(k.status)).length,
      _childError: allKids.filter(k => k.status === 'error').length,
    });
    if (isFolder && expandedSites.has(root.id)) {
      (children[root.id] || []).forEach(child => {
        rows.push({ ...child, _depth: 1, _isFolder: false });
      });
    }
  });

  // Filho cujo pai caiu no filtro continua visível — some o pai, não o dado.
  filtered.forEach(item => {
    if (!item.parent_source_id) return;
    if (roots.some(r => r.id === item.parent_source_id)) return;
    if (rows.some(r => r.id === item.id)) return;
    rows.push({ ...item, _depth: 0, _isFolder: false });
  });

  return rows;
});

const temQa = computed(
  () =>
    Array.isArray(sources.value) &&
    sources.value.some(s => s.source_type === 'qa')
);

// O CSV e montado aqui e nao no servidor: o endpoint devolve os pares em JSON, e
// assim o proxy do Rails continua sendo proxy de JSON — ver o comentario em
// `sources_qa_index`. O `montarCsvQa` e o mesmo que a importacao le, entao
// exportar e reimportar fecham o circulo (ha um teste so para essa volta).
async function exportarQa() {
  exportandoQa.value = true;
  try {
    const data = await powerDataAPI.sourcesQaIndex(accountId, props.storeId);
    const pares = data?.pairs || [];
    if (!pares.length) {
      useAlert(t('POWER_DATA.SOURCES.QA_EXPORT_VAZIO'));
      return;
    }
    // BOM na frente: sem ele o Excel em portugues abre acentuacao como lixo, e o
    // arquivo parece corrompido quando o problema e so o palpite de encoding.
    const blob = new Blob([`\ufeff${montarCsvQa(pares)}`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qa-base-${props.storeId}.csv`;
    a.click();
    // Sem isto o blob fica preso na memoria da aba ate ela ser fechada.
    URL.revokeObjectURL(url);
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.SOURCES.FETCH_ERROR'));
  } finally {
    exportandoQa.value = false;
  }
}

const hasProcessing = computed(
  () =>
    Array.isArray(sources.value) &&
    sources.value.some(s => PROCESSING_STATES.includes(s.status))
);

// ── Polling ─────────────────────────────────────────────────────
// setTimeout encadeado, não setInterval: o intervalo do setInterval é fixado na
// criação, então o backoff que existia aqui antes nunca saía de 2s.
let pollTimer = null;
const POLL_MIN = 1500;
const POLL_MAX = 15000;
let pollDelay = POLL_MIN;
// Um clearTimeout não basta para parar este poll. O reagendamento mora no `finally`, então
// uma volta que já estava em voo quando a aba saiu de cena rearma o timer depois do clear —
// medido: três buscas em 30 s com a aba escondida. Quem manda no ciclo é este sinal.
let pollPausado = false;

function scheduleNextPoll() {
  if (pollPausado) return;
  pollTimer = setTimeout(async () => {
    // try/finally: o reagendamento e a ULTIMA coisa que pode deixar de acontecer.
    // Antes, qualquer excecao no corpo pulava o `scheduleNextPoll()` do fim e o
    // poll morria de vez, sem sintoma — a tela apenas parava de se atualizar.
    try {
      if (pollPausado) return;
      if (hasProcessing.value) {
        await fetchSources();
        pollDelay = Math.min(pollDelay * 1.4, POLL_MAX);
      } else {
        pollDelay = POLL_MIN;
      }
    } finally {
      scheduleNextPoll();
    }
  }, pollDelay);
}

// ── Seleção ─────────────────────────────────────────────────────
const idsVisiveis = computed(() => treeRows.value.map(r => r.id));
const todasSelecionadas = computed(
  () =>
    idsVisiveis.value.length > 0 &&
    idsVisiveis.value.every(id => selecionadas.value.has(id))
);
const algumaSelecionada = computed(() => selecionadas.value.size > 0);

function toggleSelecao(id) {
  const prox = new Set(selecionadas.value);
  if (prox.has(id)) prox.delete(id);
  else prox.add(id);
  selecionadas.value = prox;
}

// Marcar "todas" repinta uma caixa por linha visivel — com a Le Creuset aberta
// sao 1.186 de uma vez. Mesmo tratamento do toggleSite: cede um quadro pintado
// para o indicador aparecer ANTES da espera, nao depois dela.
const selecionandoTodas = ref(false);

async function toggleTodas() {
  if (selecionandoTodas.value) return;
  selecionandoTodas.value = true;
  await proximoQuadroPintado();
  try {
    selecionadas.value = todasSelecionadas.value
      ? new Set()
      : new Set(idsVisiveis.value);
    await nextTick();
  } finally {
    selecionandoTodas.value = false;
  }
}

function limparSelecao() {
  selecionadas.value = new Set();
}

async function confirmarExclusaoEmLote() {
  const ids = [...selecionadas.value];
  if (!ids.length) return;
  isBulkDeleting.value = true;
  let falhas = 0;
  // ACESSO-01 (L8b): recusado pelo acesso, o lote inteiro é recusado igual, e a frase do servidor diz por quê.
  let recusa = '';
  // Sequencial de propósito: paralelizar 50 DELETEs já saturou o agent-admin.
  for (let i = 0; i < ids.length; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await powerDataAPI.sourcesDestroy(accountId, props.storeId, ids[i]);
    } catch (e) {
      falhas += 1;
      recusa = fraseDaRecusa(e) || recusa;
    }
  }
  isBulkDeleting.value = false;
  bulkDialogRef.value?.close();
  limparSelecao();
  await fetchSources();
  useAlert(
    recusa ||
      (falhas
        ? t('POWER_DATA.SOURCES.BULK_DELETE_PARTIAL', {
            n: ids.length - falhas,
            f: falhas,
          })
        : t('POWER_DATA.SOURCES.BULK_DELETE_DONE', { n: ids.length }))
  );
}

// ── Ações ───────────────────────────────────────────────────────
// Qual site esta abrindo/fechando agora. Guarda o id, nao um booleano, porque o
// indicador tem de aparecer NA linha clicada.
const siteEmTransicao = ref(null);

async function toggleSite(id) {
  // Enquanto um site esta em transicao, outro clique nao empilha trabalho: a
  // thread ja esta ocupada e o segundo clique so faria a espera dobrar.
  if (siteEmTransicao.value !== null) return;
  siteEmTransicao.value = id;
  await proximoQuadroPintado();
  try {
    if (expandedSites.has(id)) expandedSites.delete(id);
    else expandedSites.add(id);
    // nextTick resolve depois que o Vue aplicou o re-render — a parte cara.
    // O indicador fica de pe exatamente durante ela.
    await nextTick();
  } finally {
    siteEmTransicao.value = null;
  }
}

// Site importando: abre sozinho, para a pessoa ver as páginas mudando de
// estado. Depois de pronto, fica fechado — 300 filhas abertas é ruído.
watch(
  () =>
    sources.value
      .filter(s => s.source_type === 'site' && isProcessing(s))
      .map(s => s.id),
  ids => ids.forEach(id => expandedSites.add(id))
);

function openEdit(row) {
  editingSource.value = row;
  editDialogRef.value?.open();
}

async function onSourceSaved() {
  pollDelay = POLL_MIN;
  await fetchSources();
}

async function reprocess(row) {
  loading.value[row.id] = true;
  try {
    await powerDataAPI.sourcesReprocess(accountId, props.storeId, row.id);
    pollDelay = POLL_MIN;
    await fetchSources();
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.SOURCES.REPROCESS_ERROR'));
  } finally {
    loading.value[row.id] = false;
  }
}

async function transcrever(row) {
  loading.value[row.id] = true;
  try {
    await powerDataAPI.sourcesTranscrever(accountId, props.storeId, row.id);
    pollDelay = POLL_MIN;
    await fetchSources();
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.SOURCES.TRANSCREVER_ERROR'));
  } finally {
    loading.value[row.id] = false;
  }
}

function openDeletePopup(row) {
  sourceToDelete.value = row;
  deleteDialogRef.value?.open();
}

async function confirmDeletion() {
  const target = sourceToDelete.value;
  if (!target) return;
  loading.value[target.id] = true;
  try {
    await powerDataAPI.sourcesDestroy(accountId, props.storeId, target.id);
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.SOURCES.DELETE_ERROR'));
  } finally {
    loading.value[target.id] = false;
    sourceToDelete.value = null;
    deleteDialogRef.value?.close();
    fetchSources();
  }
}

onMounted(() => {
  fetchSources();
  scheduleNextPoll();
});

// Guardada em cache pelo <keep-alive> do Show: a aba some da tela sem ser destruída.
// Sem isto o poll continuaria rodando por trás da aba de Configurações, rebaixando a
// lista inteira a cada ciclo para uma tela que ninguém está olhando.
onDeactivated(() => {
  pollPausado = true;
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
});

onActivated(() => {
  pollPausado = false;
  // Voltar para a aba é o momento de reconciliar: enquanto ela esteve escondida o
  // processamento pode ter andado.
  fetchSources();
  pollDelay = POLL_MIN;
  scheduleNextPoll();
});

onUnmounted(() => {
  pollPausado = true;
  if (pollTimer) clearTimeout(pollTimer);
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center gap-2">
      <!-- "Todas" fica na barra, não no thead: o BaseTable só aceita texto
           como cabeçalho, e aqui é onde a ação em lote aparece. -->
      <i
        v-if="selecionandoTodas"
        v-tooltip.top="t('POWER_DATA.SOURCES.LOADING')"
        class="i-lucide-loader-circle animate-spin text-n-slate-10 ltr:ml-1 rtl:mr-1"
      />
      <Checkbox
        v-else
        v-tooltip.top="t('POWER_DATA.SOURCES.SELECT_ALL')"
        :model-value="todasSelecionadas"
        :indeterminate="algumaSelecionada && !todasSelecionadas"
        :disabled="!idsVisiveis.length"
        class="ltr:ml-1 rtl:mr-1"
        @change="toggleTodas"
      />
      <Input
        v-model="search"
        size="sm"
        class="w-56"
        :placeholder="t('POWER_DATA.SOURCES.SEARCH_PLACEHOLDER')"
      />
      <FilterPill
        v-model="filterTypeLista"
        :label="t('POWER_DATA.SOURCES.TYPE')"
        :options="typeOptions"
        single
      />
      <FilterPill
        v-model="filterStatusLista"
        :label="t('POWER_DATA.SOURCES.STATUS')"
        :options="statusOptions"
        single
      />

      <!-- Só aparece quando há Q&A na base: um botão de exportar numa base sem
           nenhum par é um botão que só sabe dizer "não tem nada". -->
      <Button
        v-if="temQa"
        v-tooltip.top="t('POWER_DATA.SOURCES.QA_EXPORT')"
        icon="i-lucide-download"
        slate
        size="sm"
        :is-loading="exportandoQa"
        @click="exportarQa"
      />

      <!-- Barra de lote: só existe enquanto houver seleção, no lugar onde a
           pessoa já está olhando. -->
      <div
        v-if="algumaSelecionada"
        class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto"
      >
        <span class="text-body-small text-n-slate-11 tabular-nums">
          {{ t('POWER_DATA.SOURCES.SELECTED', { n: selecionadas.size }) }}
        </span>
        <Button
          :label="t('POWER_DATA.SOURCES.DELETE_SELECTED')"
          icon="i-lucide-trash-2"
          ruby
          faded
          xs
          @click="bulkDialogRef?.open()"
        />
        <Button
          :label="t('POWER_DATA.CANCEL')"
          slate
          link
          xs
          @click="limparSelecao"
        />
      </div>
    </div>

    <!-- Achatada de propósito: a célula do OS tem py-3 e o cabeçalho py-4, que
         é folga para tabela de 5 linhas e desperdício para tabela de 50. Mesma
         técnica de variante que o SettingsLayout usa no thead. -->
    <!-- Enquanto a primeira carga corre, o BaseTable mostrava "nenhuma source
         nesta base ainda": o `isFetching` existia, era atualizado e nunca
         chegava ao template. Uma base com 1.186 fontes se anunciava vazia
         durante toda a espera. -->
    <div
      v-if="isFetching && !treeRows.length"
      class="flex items-center justify-center gap-2 py-20 text-body-main text-n-slate-11"
    >
      <i class="i-lucide-loader-circle animate-spin" />
      {{ t('POWER_DATA.SOURCES.LOADING') }}
    </div>
    <!-- LARGURA-COLUNAS: o `th` do BaseTable e `text-start` fixo e a tabela e
         `table-auto`, entao numa largura cheia os campos curtos boiavam longe
         uns dos outros e o numero de chunks caia colado a esquerda da coluna.
         O BaseTable/BaseTableCell sao compartilhados com 10+ telas de Settings
         (macros, automation, labels, webhooks...), entao nada disto muda la
         dentro: a celula ja aceita `align`, e o cabecalho e alinhado aqui por
         coluna. Ordem: 1 checkbox, 2 Nome, 3 Tipo, 4 Chunks, 5 Atualizada,
         6 Status, 7 acoes.
         Houve aqui um `w-full` na coluna do Nome, para ela absorver a folga de
         uma tela larga. Saiu: quem estreita agora e o PAINEL (COLUNA-PZ no
         Show.vue), entao nao ha folga sobrando para alguem absorver — e o
         `w-full` so servia para empurrar 260px de vazio entre o nome e Tipo. -->
    <BaseTable
      class="[&_td]:!py-1.5 [&_th]:!py-2 [&_th]:!text-body-small [&_th]:!font-medium [&_th:nth-child(3)]:!text-center [&_th:nth-child(4)]:!text-center [&_th:nth-child(5)]:!text-center [&_th:nth-child(6)]:!text-center [&_th:nth-child(n+3)]:whitespace-nowrap"
      :headers="tableHeaders"
      :items="treeRows"
      :loading="isFetching"
      :no-data-message="
        fetchFalhou
          ? t('POWER_DATA.SOURCES.FETCH_ERROR')
          : search || filterType || filterStatus
            ? t('POWER_DATA.SOURCES.NO_RESULTS')
            : t('POWER_DATA.SOURCES.EMPTY')
      "
    >
      <template #row="{ items }">
        <BaseTableRow v-for="row in items" :key="row.id" :item="row">
          <template #default>
            <BaseTableCell class="w-8">
              <Checkbox
                :model-value="selecionadas.has(row.id)"
                @change="toggleSelecao(row.id)"
              />
            </BaseTableCell>
            <BaseTableCell>
              <div
                class="flex items-center gap-2 min-w-0"
                :class="row._depth ? 'ltr:pl-6 rtl:pr-6' : ''"
              >
                <button
                  v-if="row._isFolder"
                  v-tooltip.top="
                    siteEmTransicao === row.id
                      ? expandedSites.has(row.id)
                        ? t('POWER_DATA.SOURCES.COLLAPSING')
                        : t('POWER_DATA.SOURCES.EXPANDING')
                      : null
                  "
                  class="text-n-slate-10 hover:text-n-slate-12 disabled:cursor-wait"
                  :disabled="siteEmTransicao !== null"
                  :aria-busy="siteEmTransicao === row.id"
                  @click="toggleSite(row.id)"
                >
                  <!-- animate-spin e transform, entao roda no compositor e
                       continua girando mesmo com a thread principal ocupada
                       montando as linhas. Um indicador que congela junto com a
                       tela nao informa nada. -->
                  <i
                    :class="
                      siteEmTransicao === row.id
                        ? 'i-lucide-loader-circle animate-spin'
                        : expandedSites.has(row.id)
                          ? 'i-lucide-chevron-down'
                          : 'i-lucide-chevron-right'
                    "
                  />
                </button>
                <i :class="iconForType(row)" class="text-n-slate-10 shrink-0" />
                <!-- `truncate` sozinho nao cortava nada: sem largura maxima a
                     coluna simplesmente crescia, e uma pergunta de Q&A com 70
                     caracteres empurrava Tipo, Chunks e Status para longe. O
                     nome inteiro nao se perde — vai para o tooltip. -->
                <span
                  v-tooltip.top="displayName(row)"
                  class="text-body-main text-n-slate-12 truncate max-w-[26rem]"
                >
                  {{ displayName(row) }}
                </span>
                <span
                  v-if="row._isFolder && row._childCount"
                  class="text-body-small text-n-slate-10"
                >
                  ({{ row._childCount }})
                </span>
              </div>
            </BaseTableCell>

            <BaseTableCell align="center">
              <span class="text-body-main text-n-slate-11">
                {{ typeLabel(row) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="center">
              <span
                v-if="row._isFolder"
                class="text-body-small text-n-slate-11 tabular-nums whitespace-nowrap"
                :class="row._childError ? 'text-n-amber-11' : ''"
              >
                {{
                  t('POWER_DATA.SOURCES.PAGES_DONE', {
                    done: row._childDone,
                    total: row._childCount,
                  })
                }}
              </span>
              <span
                v-if="row._isFolder && row._childError"
                class="ltr:ml-1 rtl:mr-1 text-body-small text-n-amber-11 tabular-nums whitespace-nowrap"
              >
                {{
                  t('POWER_DATA.SOURCES.PAGES_FAILED', { n: row._childError })
                }}
              </span>
              <span
                v-if="!row._isFolder && row.chunks_count > 0"
                class="text-body-main text-n-slate-11 tabular-nums"
              >
                {{ row.chunks_count }}
              </span>
              <span
                v-else-if="!row._isFolder"
                class="text-body-small text-n-slate-10"
              >
                —
              </span>
            </BaseTableCell>

            <BaseTableCell align="center">
              <span class="text-body-main text-n-slate-11 whitespace-nowrap">
                {{ relativeTime(row.updated_at || row.created_at) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="center">
              <!-- Em processamento o simbolo e o proprio progresso: o anel
                   marca a porcentagem que o backend grava a cada lote de
                   embedding, nao e animacao decorativa. Ela nao some com o
                   selo — vai para o tooltip, junto com o nome do estado. -->
              <span
                v-if="isProcessing(row)"
                v-tooltip.top="statusTooltip(row)"
                class="inline-flex items-center justify-center size-6 rounded-full"
                :class="statusClass(row.status)"
                role="img"
                :aria-label="statusTooltip(row)"
              >
                <svg class="size-4 -rotate-90" viewBox="0 0 20 20">
                  <circle
                    cx="10"
                    cy="10"
                    :r="RAIO_ANEL"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    class="opacity-25"
                  />
                  <circle
                    cx="10"
                    cy="10"
                    :r="RAIO_ANEL"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    :stroke-dasharray="VOLTA_ANEL"
                    :stroke-dashoffset="anelOffset(row)"
                    class="transition-[stroke-dashoffset] duration-500 ease-out"
                  />
                </svg>
              </span>
              <span
                v-else
                v-tooltip.top="statusTooltip(row)"
                class="inline-flex items-center justify-center size-6 rounded-full"
                :class="statusClass(row.status)"
                role="img"
                :aria-label="statusTooltip(row)"
              >
                <i :class="statusIcon(row.status)" class="size-3.5" />
              </span>
            </BaseTableCell>

            <BaseTableCell align="end">
              <!-- `sm` em vez de `xs`: o alvo passa de 24 para 32 px, e o icone
                   de 12 para 18. O `[&>span]:!text-[1.125rem]` mira o span que o
                   Icon renderiza — o tamanho do icone vem do font-size (1em),
                   entao mexer no botao sozinho nao adiantaria. -->
              <div class="flex justify-end flex-shrink-0 gap-1">
                <Button
                  v-tooltip.top="t('POWER_DATA.SOURCES.EDIT')"
                  icon="i-lucide-settings"
                  slate
                  sm
                  class="[&>span]:!text-[1.125rem]"
                  @click="openEdit(row)"
                />
                <Button
                  v-if="podeReprocessar(row)"
                  v-tooltip.top="t('POWER_DATA.SOURCES.REPROCESS')"
                  icon="i-lucide-refresh-cw"
                  slate
                  sm
                  class="[&>span]:!text-[1.125rem]"
                  :disabled="isProcessing(row)"
                  :is-loading="loading[row.id]"
                  @click="reprocess(row)"
                />
                <Button
                  v-if="podeTranscrever(row)"
                  v-tooltip.top="t('POWER_DATA.SOURCES.TRANSCREVER')"
                  icon="i-lucide-rotate-ccw"
                  slate
                  sm
                  class="[&>span]:!text-[1.125rem]"
                  :is-loading="loading[row.id]"
                  @click="transcrever(row)"
                />
                <Button
                  v-tooltip.top="t('POWER_DATA.SOURCES.DELETE')"
                  icon="i-lucide-trash-2"
                  slate
                  sm
                  class="[&>span]:!text-[1.125rem] hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :is-loading="loading[row.id]"
                  @click="openDeletePopup(row)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <SourceEditDialog
      ref="editDialogRef"
      :store-id="storeId"
      :source="editingSource"
      @saved="onSourceSaved"
    />

    <Dialog
      ref="bulkDialogRef"
      type="alert"
      :title="
        t('POWER_DATA.SOURCES.BULK_DELETE_TITLE', { n: selecionadas.size })
      "
      :description="
        t('POWER_DATA.SOURCES.BULK_DELETE_WARNING', { n: selecionadas.size })
      "
      :confirm-button-label="t('POWER_DATA.DELETE_YES')"
      :cancel-button-label="t('POWER_DATA.CANCEL')"
      :is-loading="isBulkDeleting"
      @confirm="confirmarExclusaoEmLote"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('POWER_DATA.SOURCES.DELETE_TITLE')"
      :description="
        t('POWER_DATA.SOURCES.DELETE_WARNING', {
          name: sourceToDelete ? displayName(sourceToDelete) : '',
        })
      "
      :confirm-button-label="t('POWER_DATA.DELETE_YES')"
      :cancel-button-label="t('POWER_DATA.CANCEL')"
      @confirm="confirmDeletion"
    />
  </div>
</template>
